import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { KeyboardController } from 'react-native-keyboard-controller';

import type { PostForm } from '@/features/post-editor/constants/form';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';

type UseEditorModalReturn = {
  /** Currently open modal section, or undefined when closed. */
  active?: PostForm;
  /** Localized title for the active section. */
  title?: string;
  isVisible: boolean;
  open: (section: PostForm) => void;
  close: () => void;
};

/**
 * Drives the single bottom modal shared by the post editor.
 *
 * Replaces the previous hand-rolled `Record<PostForm, boolean>` state plus its
 * open/close/title bookkeeping with a single active-section value.
 */
export const useEditorModal = (): UseEditorModalReturn => {
  const { t } = useTranslation(LocalizationNameSpace.POST_CREATION, {
    keyPrefix: 'screens.post-editor'
  });
  const [active, setActive] = useState<PostForm>();

  const open = useCallback((section: PostForm) => {
    KeyboardController.dismiss();
    setActive(section);
  }, []);

  const close = useCallback(() => setActive(undefined), []);

  const title = useMemo(() => (active ? t(active) : undefined), [active, t]);

  return {
    active,
    title,
    isVisible: active !== undefined,
    open,
    close
  };
};
